'use strict';

import $ from 'jquery';
import Highcharts from 'highcharts/highstock';
import BigNumber from 'bignumber.js';

/**
 * グラフJSのライブラリ一覧
 * 汎用的に使うものはここに記載
 *
 * 要事前読み込み : Highstock.js
 *                  bignumber.min.js
 */
class ChartLibrary {
    constructor() {
        /**
         * Mathクラス拡張
         */

        /**
         * 小数点の位置を取得
         *
         * @param {type} value
         * @returns {Number}
         */
        if (Math.getDecimalLength === undefined) {
            Math.getDecimalLength = function (value) {
                let list = (value + '').split('.'),
                    result = 0;

                if (list[1] !== undefined && list[1] !== null && list[1].length > 0) {
                    result = list[1].length;
                }

                return result;
            };
        }

        /**
         * 任意の桁数で四捨五入できるようにする
         * Math.exRound(123456.78, 3)  => 123000
         * Math.exRound(123456.78, -1) => 123456.8
         *
         * @param {Number} num 数値
         * @param {Integer} digit 桁数
         * @return {Number}
         */
        if (Math.exRound === undefined) {
            Math.exRound = function (num, digit) {
                num = parseFloat(num);
                num = num.toPrecision(10);
                digit = parseFloat(digit);
                const unit = Math.pow(10, digit).toPrecision(5);
                return new BigNumber(Math.round(new BigNumber(num).div(unit))).times(unit);
            };
        }

        /**
         * 切り捨て
         */
        if (Math.exFloor === undefined) {
            Math.exFloor = function (num, digit) {
                num = parseFloat(num);
                num = num.toPrecision(10);
                digit = parseFloat(digit);
                const unit = Math.pow(10, digit).toPrecision(5);
                return new BigNumber(Math.floor(new BigNumber(num).div(unit))).times(unit);
            };
        }

        /**
         * 切り上げ
         */
        if (Math.exCeil === undefined) {
            Math.exCeil = function (num, digit) {
                num = parseFloat(num);
                num = num.toPrecision(10);
                digit = parseFloat(digit);
                const unit = Math.pow(10, digit).toPrecision(5);
                return new BigNumber(Math.ceil(new BigNumber(num).div(unit))).times(unit);
            };
        }

        /**
         * Highchartsのチェックボックスを凡例の左側に移動させる
         *
         * @param {type} H
         * @returns {undefined}
         */
        (function (H) {
            const leftOffset = 55; // 数値を大きくすると左へ

            H.wrap(H.Legend.prototype, 'positionCheckboxes', function (p, scrollOffset) {
                let alignAttr = this.group.alignAttr,
                    translateY,
                    clipHeight = this.clipHeight || this.legendHeight;

                if (alignAttr) {
                    translateY = alignAttr.translateY;
                    H.each (this.allItems, function (item) {
                        let checkbox = item.checkbox,
                            bBox = item.legendItem.getBBox(true),
                            top;

                        if (checkbox) {
                            top = translateY + checkbox.y + (scrollOffset || 0) + 1;
                            H.css(checkbox, {
                                left:
                                    alignAttr.translateX +
                                    item.checkboxOffset +
                                    checkbox.x -
                                    leftOffset -
                                    bBox.width +
                                    'px',
                                top: top + 'px',
                                display:
                                    top > translateY - 6 && top < translateY + clipHeight - 6
                                        ? ''
                                        : 'none'
                            });
                        }
                    });
                }
            });
        })(Highcharts);
    }

    /**
     * Highcharts シンボルマークを拡張する
     */
    symbolFormat(Highcharts) {
        if (typeof Highcharts !== 'undefined') {
            makeCrossSymbol();
            makeTriangleLineSymbol();

            /**
             * ×マークシンボルを作成する
             */
            function makeCrossSymbol() {
                //×マークをマーカーで使えるようにする
                Highcharts.SVGRenderer.prototype.symbols.cross = function (x, y, w, h) {
                    return ['M', x, y, 'L', x + w, y + h, 'M', x + w, y, 'L', x, y + h, 'z'];
                };
                if (Highcharts.VMLRenderer) {
                    Highcharts.VMLRenderer.prototype.symbols.cross =
                        Highcharts.SVGRenderer.prototype.symbols.cross;
                }
            }
            /**
             * 三角マークシンボルを作成する
             */
            function makeTriangleLineSymbol() {
                Highcharts.SVGRenderer.prototype.symbols.triangleLine = function (x, y, w, h) {
                    return [
                        'M',
                        x + w / 2,
                        y,
                        'L',
                        x + w,
                        y + h,
                        'M',
                        x + w,
                        y + h,
                        'L',
                        x,
                        y + h,
                        'M',
                        x,
                        y + h,
                        'L',
                        x + w / 2,
                        y
                    ];
                };

                if (Highcharts.VMLRenderer) {
                    Highcharts.VMLRenderer.prototype.symbols.triangleLine =
                        Highcharts.SVGRenderer.prototype.symbols.triangleLine;
                }
            }
        }
    }

    /**
     * tooltipのpositionerを返却する
     *
     * @param {object} $this this
     * @param {integer} boxWidth
     * @param {integer} boxHeight
     * @param {object} point
     * @return {object} retPoint
     */
    tooltipPositioner($this, boxWidth, boxHeight, point) {
        try {
            // Set up the variables
            let chart = $this.chart,
                plotLeft = chart.plotLeft,
                plotTop = chart.plotTop,
                plotWidth = chart.plotWidth,
                plotHeight = chart.plotHeight,
                //tooltipとカーソルの隙間
                distance = Highcharts.pick($this.options.distance, 25), // You can use a number directly here, as you may not be able to use pick, as its an internal highchart function
                pointX = point.plotX,
                pointY = point.plotY,
                x = pointX + plotLeft + (chart.inverted ? distance : -boxWidth - distance),
                y = pointY - boxHeight + plotTop + 15, // 15 means the point is 15 pixels up from the bottom of the tooltip
                alignedRight,
                retPoint;

            // It is too far to the left, adjust it
            if (x < 7) {
                x = plotLeft + pointX + distance;
            }
            // Test to see if the tooltip is too far to the right,
            // if it is, move it back to be inside and then up to not cover the point.
            if (x + boxWidth > plotLeft + plotWidth) {
                x -= x + boxWidth - (plotLeft + plotWidth);
                y = pointY - boxHeight + plotTop - distance;
                alignedRight = true;
            }
            // If it is now above the plot area, align it to the top of the plot area
            //天井の固定位置？
            if (y < plotTop + 5) {
                y = plotTop + 5;
                // If the tooltip is still covering the point, move it below instead
                if (alignedRight && pointY >= y && pointY <= y + boxHeight) {
                    y = pointY + plotTop + distance; // below
                }
            }
            // Now if the tooltip is below the chart, move it up. It's better to cover the
            // point than to disappear outside the chart. #834.
            if (y + boxHeight > plotTop + plotHeight) {
                y = Math.max(plotTop, plotTop + plotHeight - boxHeight - distance); // below
            }
            retPoint = { x: x, y: y };

            return retPoint;
        } catch (e) {
            console.log(e);
            return { x: point.plotX, y: point.plotY };
        }
    }

    /**
     * 数値のclassを返却
     *
     * @param {type} param 数値
     * @return {String} numClass クラス名
     */
    getNumClass(param) {
        let numClass = '',
            compParam = param.replace(/,/g, '');

        if (compParam == '-') {
            numClass = 'null';
        } else if (compParam > 0) {
            numClass = 'plus';
        } else {
            numClass = 'minus';
        }

        return numClass;
    }

    /**
     * 数値をカンマ区切りにする
     *
     * @param {Integer} number
     * @param {Integer} decimals
     * @returns {String}
     */
    numberFormat(number, decimals) {
        const pattern = /^[-]?([1-9]\d*|0)$/;

        if (! pattern.test(decimals)) {
            decimals = 0;
        }

        return number === null ? '-' : Highcharts.numberFormat(number, decimals, '.', ',');
    }
    /**
     * tickPositionerに設定する値を計算する
     * jQuery1.12.3で動作確認
     * 計算にはbignumber.jsを利用する
     *   new BigNumber(num1).plus(num2).times(num3);
     *   ・足し算：.plus()
     *   ・引き算：.minus()
     *   ・掛け算：.times()
     *   ・割り算：.div()
     *
     * @param {Object} $this this
     * @param {integer} tickAmount デフォルト7
     * @param {boolean} minZeroFlg メモリ最小値を0に設定するフラグ デフォルトfalse
     * @returns {Array} positions tickPositions配列
     */
    calcTickPositions($this, tickAmount, minZeroFlg) {
        if ($this.dataMax === null || $this.dataMin === null ) {
            return;
        }

        if (minZeroFlg === undefined || $this.dataMin < 0) {
            minZeroFlg = false;
        }

        //目盛の数
        tickAmount = tickAmount || 7;

        //最小値と最大値が同値の場合、線を真ん中に寄せる
        if ($this.dataMax === $this.dataMin) {
            let retPos = [];
            let tmpData = $this.dataMax;
            let increment;

            if ((tickAmount - 1) % 2 === 0) {
                increment = (tickAmount - 1) / 2;
            } else {
                increment = (tickAmount - 2) / 2;
            }

            //データ値を中心に、positionsを作成
            for (let i = 0; i < tickAmount; i++) {
                retPos.push(parseFloat(new BigNumber(tmpData).minus(increment)));
                increment = parseFloat(new BigNumber(increment).minus(1));
            }

            return retPos;
        }

        //最小値と最大値の差 (最大値 - 最小値)
        let dataDiff = minZeroFlg ? new BigNumber($this.dataMax) : new BigNumber($this.dataMax).minus($this.dataMin),
        //let dataDiff = minZeroFlg ? new BigNumber($this.dataMax).times(3) : new BigNumber($this.dataMax).minus($this.dataMin).times(3),
        //差の1目盛あたりの値
            diffDivTick = Math.exRound(new BigNumber(dataDiff).div(tickAmount - 1), -2),
        //差の桁数
            digit = String(Math.floor(diffDivTick)).length,
        //有効桁数
            digitDivInt = parseInt(digit - 1);

        //1目盛の値が小さい時、小数点表示桁数を設定
        if (diffDivTick <= 0.5) {
            digitDivInt = -1;
        } else if (diffDivTick < 1) {
            digitDivInt = -1;
        } else if (diffDivTick > 199) {
            digitDivInt = digitDivInt - 1;
        }

        //メモリ最小値
        let floorMin = minZeroFlg ? 0 : Math.exFloor($this.dataMin, digitDivInt),
        //最大値を桁数により切り上げで均す
            ceilMax = Math.exCeil($this.dataMax, digitDivInt),
        //均した数値での差
            diff = new BigNumber(ceilMax).minus(floorMin),
            tickInterval = Math.exCeil(new BigNumber(diff).div(tickAmount - 1), digitDivInt),
        //tick値を設定する。最小値に1目盛あたりの値を足していく
            positions = [],
            position = parseFloat(floorMin),
            i = 0;

        while(i < tickAmount) {
            positions.push(position);
            position = parseFloat(new BigNumber(position).plus(tickInterval));
            i = (i+1)|0; //increment
        }
        return positions;
    }

    /**
     * 凡例の文字を置換する
     * @param {string} divId 対象のdivID
     */
    replaceLegendStr(divId) {
        let self = this;
        //凡例
        $(self.expectDivId(divId) + ' .highcharts-legend-item text > tspan').each (function() {
            $(this).text(self.replaceStr($(this).text()));
        });
    }

    /**
     * {replace}文字列を置換する
     * @param {String} value
     * @returns {String}
     */
    replaceStr(value) {
        let REPLACE_STR = '{replace}';
        return value.replace(REPLACE_STR, '');
    }

    /**
     * グラフが表示されていない時、メッセージを表示する
     * @param divId '#'無しでも可
     */
    checkGraphNoData(divId) {
        let self = this;

        if (divId === null) {
            return;
        }
        //#が先頭にない場合、付ける
        if (divId.indexOf('#') !== 0) {
            divId = '#' + divId;
        }
        setTimeout(function() {
            let chart = $(divId).highcharts(),
                legendItems = chart.legend.allItems,
                noDataFlg = true;
            $(legendItems).each (function(idx, data) {
                if (data.visible === true) {
                    noDataFlg = false;
                    return false;
                }
            });

            if (noDataFlg) {
                $(self.expectDivId(divId)).siblings('.graph_no_data').show();
            } else {
                $(self.expectDivId(divId)).siblings('.graph_no_data').hide();
            }
        }, 20);
    }

    /**
     * divIDを補正する
     * @param {String} divId
     * @returns {String}
     */
    expectDivId(divId) {
        //#が先頭にない場合、付ける
        if (divId.indexOf('#') !== 0) {
            divId = '#' + divId;
        }
        return divId;
    }

    /**
     * セグメント文字を置換
     * @param {type} value
     * @returns {unresolved}
     */
    replaceSegStr(value) {
        value = replaceStr(value);
        return value.replace('{', '･').replace('}', '');
    }

    /**
     * Highchartsオブジェクトを返却
     *
     * @returns {Highcharts.charts}
     */
    getChart(HighchartsObject) {
        return HighchartsObject.charts[HighchartsObject.charts.length - 1];
    }

    /**
     * Highchartsのレンダリング中idを返却
     *
     * @param {type} sharpFlg 先頭に#を付けるフラグ true:#を付ける false:#を付けない
     * @returns {String}
     */
    getRenderId(sharpFlg, HighchartsObject) {
        sharpFlg = sharpFlg || true;
        let chart = this.getChart(HighchartsObject);

        let renderId = sharpFlg ? '#' + chart.renderTo.id : chart.renderTo.id;

        return renderId;
    }

    /**
     * yAxls positionに利用する数値を返却
     *
     * @param {*} macdMin
     * @param {*} macdMax
     */
    getPositionNum(minNum, maxNum) {
        let tmpMin = Math.abs(minNum) * 1.1;
        let minDecimal = this.getDecimalNum(tmpMin);
        let labelMin = Math.exCeil(tmpMin, minDecimal);

        let tmpMax = Math.abs(maxNum) * 1.1;
        let maxDecimal = this.getDecimalNum(tmpMax);
        let labelMax = Math.exCeil(tmpMax, maxDecimal);

        return Math.max(labelMin, labelMax);
    }

    /**
     * 数値に合ったround decimalを取得
     *
     * @param {*} num
     */
    getDecimalNum(num) {
        let decimal = 0;

        if (num < 0.01) {
            decimal = -3;
        } else if (num < 0.1) {
            decimal = -2;
        } else if (num < 1) {
            decimal = -1;
        } else if (num < 50) {
            decimal = 0;
        } else if (num < 500) {
            decimal = 1;
        } else if (num < 5000) {
            decimal = 2;
        } else if (num < 50000) {
            decimal = 3;
        } else {
            decimal = 4;
        }

        return decimal;
    }

    /**
     * チャート用Jsonのダミー空データを作成
     *
     * IPO銘柄上場当日など、前日までのチャートデータがS3に存在しない場合、
     * data部を日足・週足・月足用20分ディレイデータを追加して利用
     */
    makeEmptyChartJsonList() {
        return {
            "daily": [
                {
                    "name": "株価",
                    "type": "candlestick",
                    "id": "stockPriceDaily",
                    "data": null,
                    "decimals": 1
                },
                {
                    "linkedTo": "stockPriceDaily",
                    "type": "sma",
                    "id": "sma1",
                    "name": "移動平均(5)",
                    "params": {
                        "index": 3,
                        "period": 5
                    },
                    "decimals": 1,
                    "color": "#f58127",
                    "visible": false
                },
                {
                    "linkedTo": "stockPriceDaily",
                    "type": "sma",
                    "id": "sma2",
                    "name": "移動平均(25)",
                    "params": {
                        "index": 3,
                        "period": 25
                    },
                    "decimals": 1,
                    "color": "#9041a5",
                    "visible": false
                },
                {
                    "linkedTo": "stockPriceDaily",
                    "type": "sma",
                    "id": "sma3",
                    "name": "移動平均(75)",
                    "params": {
                        "index": 3,
                        "period": 75
                    },
                    "decimals": 1,
                    "color": "#34a734",
                    "visible": false
                },
                {
                    "linkedTo": "stockPriceDaily",
                    "type": "bb",
                    "id": "bb1",
                    "name": "ボリンジャー(25)",
                    "color": "#9041a5",
                    "lineWidth": 1,
                    "topLine": {
                        "styles": {
                            "lineColor": "#f58127"
                        }
                    },
                    "bottomLine": {
                        "styles": {
                            "lineColor": "#f58127"
                        }
                    },
                    "params": {
                        "index": 3,
                        "period": 25,
                        "standardDeviation": 1
                    },
                    "decimals": 1,
                    "visible": false
                },
                {
                    "linkedTo": "stockPriceDaily",
                    "type": "bb",
                    "id": "bb2",
                    "name": "ボリンジャー(25)",
                    "color": "#9041a5",
                    "lineWidth": 0,
                    "topLine": {
                        "styles": {
                            "lineColor": "#f58127"
                        }
                    },
                    "bottomLine": {
                        "styles": {
                            "lineColor": "#f58127"
                        }
                    },
                    "params": {
                        "index": 3,
                        "period": 25,
                        "standardDeviation": 2
                    },
                    "decimals": 1,
                    "visible": false
                },
                {
                    "linkedTo": "stockPriceDaily",
                    "type": "bb",
                    "id": "bb3",
                    "name": "ボリンジャー(25)",
                    "color": "#9041a5",
                    "lineWidth": 0,
                    "topLine": {
                        "styles": {
                            "lineColor": "#f58127"
                        }
                    },
                    "bottomLine": {
                        "styles": {
                            "lineColor": "#f58127"
                        }
                    },
                    "params": {
                        "index": 3,
                        "period": 25,
                        "standardDeviation": 3
                    },
                    "decimals": 1,
                    "visible": false
                },
                {
                    "data": null,
                    "type": "column",
                    "id": "volume",
                    "name": "出来高",
                    "decimals": 0,
                    "yAxis": 1,
                    "selected": true
                },
                {
                    "linkedTo": "stockPriceDaily",
                    "type": "macd",
                    "id": "macd",
                    "name": "MACD(12,26,9)",
                    "color": "#ee0101",
                    "negativeColor": "#009acd",
                    "macdLine": {
                        "styles": {
                            "lineColor": "#d72e61"
                        }
                    },
                    "signalLine": {
                        "styles": {
                            "lineColor": "#447fa0"
                        }
                    },
                    "yAxis": 1,
                    "params": {
                        "index": 3,
                        "shortPeriod": 12,
                        "longPeriod": 26,
                        "signalPeriod": 9,
                        "period": 26
                    },
                    "decimals": 2,
                    "visible": false
                },
                {
                    "linkedTo": "stockPriceDaily",
                    "type": "rsi",
                    "id": "rsi",
                    "name": "RSI(14)",
                    "color": "#447fa0",
                    "yAxis": 1,
                    "params": {
                        "period": 14,
                        "decimals": 8
                    },
                    "decimals": 2,
                    "visible": false
                }
            ],
            "weekly": [
                {
                    "name": "株価",
                    "type": "candlestick",
                    "id": "stockPriceWeekly",
                    "data": null,
                    "decimals": 1
                },
                {
                    "linkedTo": "stockPriceWeekly",
                    "type": "sma",
                    "id": "sma1",
                    "name": "移動平均(5)",
                    "params": {
                        "index": 3,
                        "period": 5
                    },
                    "decimals": 1,
                    "color": "#f58127",
                    "visible": false
                },
                {
                    "linkedTo": "stockPriceWeekly",
                    "type": "sma",
                    "id": "sma2",
                    "name": "移動平均(13)",
                    "params": {
                        "index": 3,
                        "period": 13
                    },
                    "decimals": 1,
                    "color": "#9041a5",
                    "visible": false
                },
                {
                    "linkedTo": "stockPriceWeekly",
                    "type": "sma",
                    "id": "sma3",
                    "name": "移動平均(26)",
                    "params": {
                        "index": 3,
                        "period": 26
                    },
                    "decimals": 1,
                    "color": "#34a734",
                    "visible": false
                },
                {
                    "linkedTo": "stockPriceWeekly",
                    "type": "bb",
                    "id": "bb1",
                    "name": "ボリンジャー(13)",
                    "color": "#9041a5",
                    "lineWidth": 1,
                    "topLine": {
                        "styles": {
                            "lineColor": "#f58127"
                        }
                    },
                    "bottomLine": {
                        "styles": {
                            "lineColor": "#f58127"
                        }
                    },
                    "params": {
                        "index": 3,
                        "period": 13,
                        "standardDeviation": 1
                    },
                    "decimals": 1,
                    "visible": false
                },
                {
                    "linkedTo": "stockPriceWeekly",
                    "type": "bb",
                    "id": "bb2",
                    "name": "ボリンジャー(13)",
                    "color": "#9041a5",
                    "lineWidth": 0,
                    "topLine": {
                        "styles": {
                            "lineColor": "#f58127"
                        }
                    },
                    "bottomLine": {
                        "styles": {
                            "lineColor": "#f58127"
                        }
                    },
                    "params": {
                        "index": 3,
                        "period": 13,
                        "standardDeviation": 2
                    },
                    "decimals": 1,
                    "visible": false
                },
                {
                    "linkedTo": "stockPriceWeekly",
                    "type": "bb",
                    "id": "bb3",
                    "name": "ボリンジャー(13)",
                    "color": "#9041a5",
                    "lineWidth": 0,
                    "topLine": {
                        "styles": {
                            "lineColor": "#f58127"
                        }
                    },
                    "bottomLine": {
                        "styles": {
                            "lineColor": "#f58127"
                        }
                    },
                    "params": {
                        "index": 3,
                        "period": 13,
                        "standardDeviation": 3
                    },
                    "decimals": 1,
                    "visible": false
                },
                {
                    "data": null,
                    "type": "column",
                    "id": "volume",
                    "name": "出来高",
                    "decimals": 0,
                    "yAxis": 1,
                    "selected": true
                },
                {
                    "linkedTo": "stockPriceWeekly",
                    "type": "macd",
                    "id": "macd",
                    "name": "MACD(12,26,9)",
                    "color": "#ee0101",
                    "negativeColor": "#009acd",
                    "macdLine": {
                        "styles": {
                            "lineColor": "#d72e61"
                        }
                    },
                    "signalLine": {
                        "styles": {
                            "lineColor": "#447fa0"
                        }
                    },
                    "yAxis": 1,
                    "params": {
                        "index": 3,
                        "shortPeriod": 12,
                        "longPeriod": 26,
                        "signalPeriod": 9,
                        "period": 26
                    },
                    "decimals": 2,
                    "visible": false
                },
                {
                    "linkedTo": "stockPriceWeekly",
                    "type": "rsi",
                    "id": "rsi",
                    "name": "RSI(14)",
                    "color": "#447fa0",
                    "yAxis": 1,
                    "params": {
                        "period": 14,
                        "decimals": 8
                    },
                    "decimals": 2,
                    "visible": false
                }
            ],
            "monthly": [
                {
                    "name": "株価",
                    "type": "candlestick",
                    "id": "stockPriceMonthly",
                    "data": null,
                    "decimals": 1
                },
                {
                    "linkedTo": "stockPriceMonthly",
                    "type": "sma",
                    "id": "sma1",
                    "name": "移動平均(6)",
                    "params": {
                        "index": 3,
                        "period": 6
                    },
                    "decimals": 1,
                    "color": "#f58127",
                    "visible": false
                },
                {
                    "linkedTo": "stockPriceMonthly",
                    "type": "sma",
                    "id": "sma2",
                    "name": "移動平均(12)",
                    "params": {
                        "index": 3,
                        "period": 12
                    },
                    "decimals": 1,
                    "color": "#9041a5",
                    "visible": false
                },
                {
                    "linkedTo": "stockPriceMonthly",
                    "type": "sma",
                    "id": "sma3",
                    "name": "移動平均(24)",
                    "params": {
                        "index": 3,
                        "period": 24
                    },
                    "decimals": 1,
                    "color": "#34a734",
                    "visible": false
                },
                {
                    "linkedTo": "stockPriceMonthly",
                    "type": "bb",
                    "id": "bb1",
                    "name": "ボリンジャー(12)",
                    "color": "#9041a5",
                    "lineWidth": 1,
                    "topLine": {
                        "styles": {
                            "lineColor": "#f58127"
                        }
                    },
                    "bottomLine": {
                        "styles": {
                            "lineColor": "#f58127"
                        }
                    },
                    "params": {
                        "index": 3,
                        "period": 12,
                        "standardDeviation": 1
                    },
                    "decimals": 1,
                    "visible": false
                },
                {
                    "linkedTo": "stockPriceMonthly",
                    "type": "bb",
                    "id": "bb2",
                    "name": "ボリンジャー(12)",
                    "color": "#9041a5",
                    "lineWidth": 0,
                    "topLine": {
                        "styles": {
                            "lineColor": "#f58127"
                        }
                    },
                    "bottomLine": {
                        "styles": {
                            "lineColor": "#f58127"
                        }
                    },
                    "params": {
                        "index": 3,
                        "period": 12,
                        "standardDeviation": 2
                    },
                    "decimals": 1,
                    "visible": false
                },
                {
                    "linkedTo": "stockPriceMonthly",
                    "type": "bb",
                    "id": "bb3",
                    "name": "ボリンジャー(12)",
                    "color": "#9041a5",
                    "lineWidth": 0,
                    "topLine": {
                        "styles": {
                            "lineColor": "#f58127"
                        }
                    },
                    "bottomLine": {
                        "styles": {
                            "lineColor": "#f58127"
                        }
                    },
                    "params": {
                        "index": 3,
                        "period": 12,
                        "standardDeviation": 3
                    },
                    "decimals": 1,
                    "visible": false
                },
                {
                    "data": null,
                    "type": "column",
                    "id": "volume",
                    "name": "出来高",
                    "decimals": 0,
                    "yAxis": 1,
                    "selected": true
                },
                {
                    "linkedTo": "stockPriceMonthly",
                    "type": "macd",
                    "id": "macd",
                    "name": "MACD(12,26,9)",
                    "color": "#ee0101",
                    "negativeColor": "#009acd",
                    "macdLine": {
                        "styles": {
                            "lineColor": "#d72e61"
                        }
                    },
                    "signalLine": {
                        "styles": {
                            "lineColor": "#447fa0"
                        }
                    },
                    "yAxis": 1,
                    "params": {
                        "index": 3,
                        "shortPeriod": 12,
                        "longPeriod": 26,
                        "signalPeriod": 9,
                        "period": 26
                    },
                    "decimals": 2,
                    "visible": false
                },
                {
                    "linkedTo": "stockPriceMonthly",
                    "type": "rsi",
                    "id": "rsi",
                    "name": "RSI(14)",
                    "color": "#447fa0",
                    "yAxis": 1,
                    "params": {
                        "period": 14,
                        "decimals": 8
                    },
                    "decimals": 2,
                    "visible": false
                }
            ]
        };
    }
}

export const chartLib = new ChartLibrary();
